import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { environment } from '../../environments/environment';
import { ClientSelectionService } from '../components/clients/client-selection.service';
import { Adjuster, AdjusterApiResponse, AdjustersResult } from '../models/adjuster-management.model';

@Injectable({
  providedIn: 'root'
})
export class AdjusterManagementService {

  private readonly endpoint = 'adjuster-management/adjusters';
  private readonly sortFieldMap: Record<string, string> = {
    office: 'Office',
    team: 'Team',
    firstName: 'FirstName',
    lastName: 'LastName',
    email: 'Email',
    adjusterKey: 'AdjusterKey',
    userName: 'UserName',
    telephone: 'Telephone'
  };

  constructor(
    private http: HttpClient,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private clientSelectionService: ClientSelectionService
  ) {}

  getAdjusters(
    skip = 0,
    count = 10,
    sortBy?: string,
    sortDir?: 'asc' | 'desc',
    filters?: {
      firstName?: string;
      lastName?: string;
      userName?: string;
      email?: string;
    }
  ): Observable<AdjustersResult> {

    let params = new HttpParams()
      .set('skip', skip.toString())
      .set('count', count.toString())
      .set('sortField', this.toApiSortField(sortBy ?? ''))
      .set('sortDirection', sortDir ?? '');

    if (filters?.firstName?.trim()) {
      params = params.set('firstName', filters.firstName.trim());
    }

    if (filters?.lastName?.trim()) {
      params = params.set('lastName', filters.lastName.trim());
    }

    if (filters?.userName?.trim()) {
      params = params.set('userName', filters.userName.trim());
    }

    if (filters?.email?.trim()) {
      params = params.set('email', filters.email.trim());
    }

    return this.http
      .get<{ adjusters: AdjusterApiResponse[]; totalCount: number }>(this.getUrl(), {
        headers: this.getHeaders(),
        params
      })
      .pipe(
        map(response => ({
          adjusters: (response.adjusters ?? []).map(a => this.mapAdjuster(a)),
          totalCount: response.totalCount ?? 0
        }))
      );
  }

  deleteAdjuster(adjusterId: number): Observable<any> {
    return this.http.delete<any>(
      this.getUrl(),
      {
        headers: this.getHeaders(),
        params: { adjusterID: adjusterId.toString() }
      }
    );
  }

  private getUrl(): string {
    return `${environment.apiUrl}${this.endpoint}`;
  }

  private getHeaders(): HttpHeaders {

    const token = this.oktaAuth.getAccessToken();
    const clientDbName = this.clientSelectionService.getSelectedClientDbName();

    if (!token) {
      throw new Error('No access token found');
    }

    return new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Authorization: `Bearer ${token}`,
      ClientDBName: clientDbName,
    });
  }

  private toApiSortField(sortBy: string): string {
    return this.sortFieldMap[sortBy] ?? sortBy;
  }

  private mapAdjuster(a: AdjusterApiResponse): Adjuster {
    return {
      adjusterID: a.AdjusterID,
      office: a.Office,
      team: a.Team,
      firstName: a.FirstName,
      lastName: a.LastName,
      email: a.Email,
      adjusterKey: a.AdjusterKey,
      userName: a.UserName,
      telephone: a.Telephone
    };
  }
}